import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/api/client";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";

const EMPTY = { code: "", label_ja: "", label_en: "" };

export default function ExpenseCategoriesPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showAll, setShowAll] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = () =>
    api.get(`/expense-categories?active_only=${!showAll}`)
      .then((r) => setItems(r.data))
      .catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showAll]);

  const flash = (text) => { setMsg(text); setTimeout(() => setMsg(""), 1500); };

  const create = async () => {
    setErr("");
    if (!form.code.trim() || !form.label_ja.trim() || !form.label_en.trim()) {
      setErr(t("admin.catAllFieldsRequired"));
      return;
    }
    try {
      await api.post("/expense-categories", form);
      setForm(EMPTY); flash(t("admin.catCreated")); load();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const removeCat = async (row) => {
    if (!window.confirm(t("admin.catConfirmDelete", { name: row.label_ja }))) return;
    try {
      const { data } = await api.delete(`/expense-categories/${row.id}`);
      flash(data.was_in_use ? t("admin.catDeletedInUse") : t("admin.catDeleted"));
      load();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const move = async (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try {
      await api.post("/expense-categories/reorder", { order: next.map((c) => c.id) });
    } catch { load(); }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/expense-categories/${row.id}`, { active: !row.active });
      load();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div>
      <h1 className="page-title">{t("admin.expenseCategories")}</h1>
      <p className="page-sub">{t("admin.expenseCategoriesSub")}</p>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body">
          <div className="card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Plus size={16} /> {t("admin.catNew")}
          </div>
          <div className="grid grid-3">
            <div className="field">
              <label className="label">{t("admin.catCode")}</label>
              <input className="input" data-testid="cat-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. parking" />
              <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>{t("admin.catCodeHint")}</div>
            </div>
            <div className="field">
              <label className="label">{t("admin.catLabelJa")}</label>
              <input className="input" data-testid="cat-label-ja" value={form.label_ja} onChange={(e) => setForm({ ...form, label_ja: e.target.value })} placeholder="例: 駐車料金" />
            </div>
            <div className="field">
              <label className="label">{t("admin.catLabelEn")}</label>
              <input className="input" data-testid="cat-label-en" value={form.label_en} onChange={(e) => setForm({ ...form, label_en: e.target.value })} placeholder="e.g. Parking" />
            </div>
          </div>
          {err && <div style={{ color: "#DC2626", fontSize: 13, marginBottom: 8 }} data-testid="cat-err">{err}</div>}
          {msg && <div style={{ color: "#16A34A", fontSize: 13, marginBottom: 8 }} data-testid="cat-msg">{msg}</div>}
          <div className="row-actions">
            <button className="btn btn-primary" onClick={create} data-testid="cat-save">{t("common.save")}</button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div className="card-title" style={{ margin: 0 }}>{t("admin.catList")} ({items.length})</div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="cat-show-all" />
              {t("admin.catShowInactive")}
            </label>
          </div>
          <div className="table-wrap">
            <table className="table" data-testid="cat-table">
              <thead><tr>
                <th style={{ width: 80 }}>#</th>
                <th>{t("admin.catCode")}</th>
                <th>{t("admin.catLabelJa")}</th>
                <th>{t("admin.catLabelEn")}</th>
                <th>{t("common.status")}</th>
                <th style={{ width: 180 }}>{t("common.actions")}</th>
              </tr></thead>
              <tbody>
                {items.map((c, idx) => (
                  <tr key={c.id} style={c.active ? {} : { opacity: 0.55 }} data-testid={`cat-row-${c.id}`}>
                    <td>{c.sort_order}</td>
                    <td><code style={{ background: "#F1F5F9", padding: "2px 6px", borderRadius: 4 }}>{c.code}</code></td>
                    <td>{c.label_ja}</td>
                    <td>{c.label_en}</td>
                    <td><span className={`tag tag-${c.active ? "approved" : "draft"}`}>{c.active ? t("admin.activate") : t("admin.deactivate")}</span></td>
                    <td>
                      <div className="row-actions">
                        <button className="btn btn-outline" style={{ padding: "6px 8px", fontSize: 12 }} onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`cat-up-${c.id}`}><ChevronUp size={12}/></button>
                        <button className="btn btn-outline" style={{ padding: "6px 8px", fontSize: 12 }} onClick={() => move(idx, 1)} disabled={idx === items.length - 1} data-testid={`cat-down-${c.id}`}><ChevronDown size={12}/></button>
                        {c.active ? (
                          <button className="btn btn-danger" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => removeCat(c)} data-testid={`cat-del-${c.id}`}><Trash2 size={12}/></button>
                        ) : (
                          <button className="btn btn-success" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => toggleActive(c)} data-testid={`cat-restore-${c.id}`}>{t("admin.activate")}</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan="6" style={{ textAlign: "center", color: "#64748B" }}>—</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
